import { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { setStatusSchema } from '../../../../shared/validators/schemas/set-status.schema';
import { AccountRecordDto } from '../../dtos/account-record.dto';
import { AccountActionEnum } from '../../enums/account-action.enum';
import { AccountStatusEnum } from '../../enums/account-status.enum';

/**
 * Validates if an action can be performed by validating the record data.
 * @param record - The record for the data validation.
 * @param action - The action to be validated.
 * @param abortEarly - Whether to abort the validation on the first error.
 * @returns The validation result.
 */
export const canPerformAction = (
	record: AccountRecordDto,
	action: AccountActionEnum,
	abortEarly: boolean = false,
): ValidationFunctionResult => {
	// Check if record is provided
	if (!record) throw new Error('record is required.');

	let validationResult: ValidationResult | undefined;

	const fixedStatuses = [
		AccountStatusEnum.ACTIVE,
		AccountStatusEnum.CLOSED,
		AccountStatusEnum.ON_HOLD,
	];

	switch (action) {
		case AccountActionEnum.ACTIVATE:
			validationResult = setStatusSchema(
				AccountStatusEnum.ACTIVE,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case AccountActionEnum.CLOSE:
			validationResult = setStatusSchema(
				AccountStatusEnum.CLOSED,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case AccountActionEnum.PUT_ON_HOLD:
			validationResult = setStatusSchema(
				AccountStatusEnum.ON_HOLD,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		default:
			throw new Error(`Unknown action: ${action}`);
	}

	// Check for validation errors
	if (validationResult?.error) {
		return {
			isValid: false,
			errors: validationResult.error.details?.map((e) => e.message),
		};
	}

	// Return valid result if no errors
	return { isValid: true };
};
